import { IRotation } from '@clickster/constant-definitions/dist-esm/entities/rotation/types';
import { ANALYTICS, API_V2 } from '../lib/axios';
import { fetchAllPaginated } from './utils/fetch-all-paginated';
import { IReport } from '@clickster/business-logic/dist-esm/reports/types/analytics';
import { IRotationAnalytics } from '../interfaces/rotations';

const ROTATIONS_ENDPOINT = '/rotations';

const list = async (params = {}) => {
  return fetchAllPaginated<IRotation>(API_V2, ROTATIONS_ENDPOINT, { populate: 'vertical', ...params });
};

const findById = async ({ id, data, params = {} }: { id: string; data?: any; params?: {} }) => {
  const response = await API_V2.post<IRotation>(`${ROTATIONS_ENDPOINT}/${id}`, data, {
    ...params,
  });
  return response.data;
};

/**
 * Fetches a single rotation by ID using GET (lightweight).
 * @param {string} id - The rotation ID.
 * @param {Record<string, string>} params - Query params (e.g. { select: 'name' }).
 * @returns {Promise<Partial<IRotation>>} The rotation data with selected fields.
 */
const getById = async (id: string, params: Record<string, string> = {}) => {
  const response = await API_V2.get<Partial<IRotation>>(`${ROTATIONS_ENDPOINT}/${id}`, { params: { populate: 'vertical', ...params } });
  return response.data;
};


const update = async (data: IRotation) => {
  const rotation = await API_V2.put(`${ROTATIONS_ENDPOINT}/${data._id}`, data, {});
  return rotation.data;
};

const countries = async () => {
  const response = await API_V2.get<{ code: string; name: string; regions: { href: string } }[]>(
    '/countries',
  );
  return response.data;
}

const regionsByCountry = async (code: string) => {
  const response = await API_V2.get<{ countryCode: string; regionCode: string; regionName: string }[]>(
    `/countries/${code}/regions`,
  );
  return response.data;
};

const citiesByRegion = async (countryName: string, regionName: string) => {
  const response = await API_V2.post<any[]>(`/cities`, {
    country: countryName,
    region: regionName,
  });
  return response.data;
};

const create = async (data: IRotation) => {
  const response = await API_V2.post<IRotation>(`${ROTATIONS_ENDPOINT}`, data, {});
  return response.data;
};

/**
 * Run server-side EPC optimization for the specified specs within a rotation.
 * Fetches drilldown data for the given date range and returns per-spec integer
 * weight distributions calculated using the epsilon-greedy algorithm.
 *
 * @param params.rotationId  The rotation ID to optimize
 * @param params.specIds     Array of spec IDs to calculate distributions for
 * @param params.dates       Date range and timezone info for the drilldown query
 * @returns Per-spec distributions: Record<specId, { offerId, value }[]>
 */
export interface EpcOfferMetadata {
  offerId: string;
  offerName: string;
  weight: number;
  drilldown: {
    epc: number;
    offerClicks: number;
    cvrs: number;
    clicks: number;
    matched: boolean;
  };
  significance: {
    isSignificant: boolean;
    offerCR: number;
    requiredCR: number;
  };
  breakdown: {
    explorationShare: number;
    exploitationShare: number;
  };
}

export interface EpcSpecMetadata {
  specId: string;
  specName: string;
  totalOffers: number;
  activeOffers: number;
  significantOffers: number;
  explorationPool: number;
  exploitationPool: number;
  offers: EpcOfferMetadata[];
}

export interface EpcOptimizeResponse {
  distributions: Record<string, { offerId: string; value: number }[]>;
  metadata: Record<string, EpcSpecMetadata>;
}

const optimizeByEpc = async (params: {
  rotationId: string;
  ruleId: string;
  specIds: string[];
  dates: { from: string; to: string; time_offset: number; moment_name: string };
}): Promise<EpcOptimizeResponse> => {
  const response = await API_V2.post<EpcOptimizeResponse>(
    `${ROTATIONS_ENDPOINT}/${params.rotationId}/epc-optimize`,
    {
      ruleId: params.ruleId,
      specIds: params.specIds,
      dates: params.dates,
    },
  );
  return response.data;
};

const getRotationAnalyticsById = async (params: {
  rotationId: string;
  report: Pick<IReport, 'filters' | 'dates'>
}) => {
  const response = await ANALYTICS.post<IRotationAnalytics>(`/api/v1/rotations/${params.rotationId}`, params.report);
  return response.data;
};

export const RotationsService = {
  list,
  findById,
  getById,
  update,
  countries,
  regionsByCountry,
  citiesByRegion,
  create,
  optimizeByEpc,
  getRotationAnalyticsById,
};

